import React, { ReactNode, useState, useMemo } from 'react';
import {
    Box,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Toolbar,
    Typography,
    IconButton,
    Collapse,
    Theme,
    LinearProgress
} from '@mui/material';
import { KeyboardArrowDown, KeyboardArrowUp, ArrowUpward, ArrowDownward } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { WcnCheckbox } from '@/components';

function sortData<T>(data: T[], sortBy: string, sortDirection: 'asc' | 'desc'): T[] {
    if (!sortBy) return data;

    const getNestedValue = (obj: any, path: string): any => {
        if (!path.includes('.')) {
            return obj[path];
        }
        const keys = path.split('.');
        return keys.reduce((value, key) => value?.[key], obj);
    };

    const compareValues = (aVal: any, bVal: any, isAscending: boolean): number => {
        if (aVal == null) return 1;
        if (bVal == null) return -1;

        if (typeof aVal === 'string' && typeof bVal === 'string') {
            return isAscending ? aVal.localeCompare(bVal) : bVal.localeCompare(aVal);
        }

        const result = aVal > bVal ? 1 : -1;
        return isAscending ? result : -result;
    };

    const sorted = [...data];
    sorted.sort((a: any, b: any) => {
        const aVal = getNestedValue(a, sortBy);
        const bVal = getNestedValue(b, sortBy);
        return compareValues(aVal, bVal, sortDirection === 'asc');
    });

    return sorted;
}

export interface DataTableColumn<T = any> {
    text: string | ReactNode;
    value: string;
    width?: number | string;
    sortable?: boolean;
    render?: (row: T, index: number) => ReactNode;
    align?: 'left' | 'center' | 'right' | 'inherit' | 'justify';
    title?: string | ((row: T) => string);
}

export interface DataTablePagination {
    count: number;
    page?: number;
    rowsPerPage?: number;
    onPageChange?: (event: unknown, newPage: number) => void;
    onRowsPerPageChange?: (event: React.ChangeEvent<HTMLInputElement>) => void;
    rowsPerPageOptions?: number[];
}

export interface DataTableSorting {
    sortBy: string;
    sortDirection: 'asc' | 'desc';
    onSort: (columnValue: string) => void;
}

export interface DataTableSelection<T = any> {
    selectedItems: T[];
    onSelectionChange: (items: T[]) => void;
    getItemKey: (item: T) => string | number;
    selectionMode?: 'single' | 'multiple';
}

export interface DataTableExpansion<T = any, TChild = any> {
    expandedRows?: (string | number)[];
    onExpandToggle?: (rowKey: string | number) => void;
    getRowKey: (item: T) => string | number;
    renderExpandedContent?: (row: T) => ReactNode;
    showExpandIconCondition?: (row: T) => boolean;

    // Nested table configuration (alternative to renderExpandedContent)
    nestedTable?: {
        columns: DataTableColumn<TChild>[];
        data: TChild[];
        getChildRows: (parentRow: T, allData: TChild[]) => TChild[];
        size?: 'small' | 'medium';
        noDataMessage?: string;
        paddingLeft?: number;
        expansion?: Omit<DataTableExpansion<TChild>, 'expandedRows' | 'onExpandToggle'>;
    };
}

export interface WcnTableProps<T = any> {
    readonly title?: string;
    readonly columns: DataTableColumn<T>[];
    readonly data?: T[];
    readonly loading?: boolean;
    readonly pagination?: DataTablePagination;
    readonly sorting?: DataTableSorting;
    readonly selection?: DataTableSelection<T>;
    readonly expansion?: DataTableExpansion<T>;
    readonly noDataMessage?: string;
    readonly onRowClick?: (row: T, index: number) => void;
    readonly getRowProps?: (row: T, index: number) => Record<string, any>;
    readonly size?: 'small' | 'medium';
    readonly renderEmpty?: () => ReactNode;
    readonly renderLoading?: () => ReactNode;
}

const WcnTable = <T,>({
    title,
    columns,
    data = [],
    loading = false,
    pagination,
    sorting,
    selection,
    expansion,
    noDataMessage,
    onRowClick,
    getRowProps,
    size = 'small',
    renderEmpty
}: Readonly<WcnTableProps<T>>) => {
    const { t } = useTranslation();

    const [page, setPage] = useState(pagination?.page ?? 0);
    const [rowsPerPage, setRowsPerPage] = useState(pagination?.rowsPerPage ?? 5);
    const [internalExpandedRows, setInternalExpandedRows] = useState<(string | number)[]>([]);
    const [internalSortBy, setInternalSortBy] = useState<string>('');
    const [internalSortDirection, setInternalSortDirection] = useState<'asc' | 'desc'>('asc');

    const activeSortBy = sorting?.sortBy ?? internalSortBy;
    const activeSortDirection = sorting?.sortDirection ?? internalSortDirection;

    const handleInternalSort = (columnValue: string) => {
        if (internalSortBy === columnValue) {
            setInternalSortDirection((prev) => (prev === 'asc' ? 'desc' : 'asc'));
        } else {
            setInternalSortBy(columnValue);
            setInternalSortDirection('asc');
        }
    };

    // Use external expansion state if provided, otherwise use internal state
    const expandedRows = expansion?.expandedRows ?? internalExpandedRows;
    const handleExpandToggle =
        expansion?.onExpandToggle ??
        ((rowKey: string | number) => {
            setInternalExpandedRows((prev) =>
                prev.includes(rowKey) ? prev.filter((k) => k !== rowKey) : [...prev, rowKey]
            );
        });

    const sortedData = useMemo(() => {
        if (!columns || columns.length === 0) {
            return data;
        }

        const hasSortableColumns = columns.some((col) => col.sortable !== false);
        if (!hasSortableColumns || !activeSortBy) {
            return data;
        }

        return sortData(data, activeSortBy, activeSortDirection);
    }, [data, activeSortBy, activeSortDirection, columns]);

    // Slice data for pagination if pagination is enabled and handlers are not provided
    const paginatedData = useMemo(() => {
        if (!pagination || pagination.onPageChange) {
            return sortedData;
        }
        const currentPage = pagination.page ?? page;
        const currentRowsPerPage = pagination.rowsPerPage ?? rowsPerPage;
        return sortedData.slice(currentPage * currentRowsPerPage, (currentPage + 1) * currentRowsPerPage);
    }, [sortedData, pagination, page, rowsPerPage]);

    // Check if any row can actually expand
    const hasExpandableRows = useMemo(() => {
        if (!expansion || !data.length) return false;
        if (!expansion.showExpandIconCondition) return true; // No condition means all can expand
        return data.some((row) => expansion.showExpandIconCondition!(row));
    }, [expansion, data]);

    const colSpan = useMemo(() => {
        let count = columns.length;
        if (selection) count += 1;
        if (hasExpandableRows) count += 1;
        return count;
    }, [columns.length, selection, hasExpandableRows]);

    const isRowSelected = (row: T): boolean => {
        if (!selection) return false;
        const rowKey = selection.getItemKey(row);
        return selection.selectedItems.some((item) => selection.getItemKey(item) === rowKey);
    };

    const isRowExpanded = (row: T): boolean => {
        if (!expansion) return false;
        const rowKey = expansion.getRowKey(row);
        return expandedRows.includes(rowKey);
    };

    const handleRowSelection = (row: T, event?: React.MouseEvent) => {
        if (!selection) return;
        event?.stopPropagation();
        const rowKey = selection.getItemKey(row);
        const isSelected = isRowSelected(row);
        if (selection.selectionMode === 'single') {
            selection.onSelectionChange(isSelected ? [] : [row]);
        } else if (isSelected) {
            selection.onSelectionChange(
                selection.selectedItems.filter((item) => selection.getItemKey(item) !== rowKey)
            );
        } else {
            selection.onSelectionChange([...selection.selectedItems, row]);
        }
    };

    const handleRowExpansion = (row: T, event?: React.MouseEvent) => {
        if (!expansion) return;
        event?.stopPropagation();
        handleExpandToggle(expansion.getRowKey(row));
    };

    const handleSort = (columnValue: string) => {
        if (sorting?.onSort) {
            sorting.onSort(columnValue);
        } else {
            handleInternalSort(columnValue);
        }
    };

    const handlePageChange = (_event: unknown, newPage: number) => {
        setPage(newPage);
    };

    const handleRowsPerPageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        setRowsPerPage(Number.parseInt(e.target.value, 10));
        setPage(0);
    };

    const renderEmptyState = () => {
        if (renderEmpty) {
            return (
                <TableBody>
                    <TableRow>
                        <TableCell colSpan={colSpan} align="center">
                            {renderEmpty()}
                        </TableCell>
                    </TableRow>
                </TableBody>
            );
        }
        return (
            <TableBody>
                <TableRow>
                    <TableCell colSpan={colSpan} align="center">
                        <Typography variant="body2" color="text.secondary">
                            {noDataMessage || t('noData')}
                        </Typography>
                    </TableCell>
                </TableRow>
            </TableBody>
        );
    };

    const renderTableHead = () => (
        <TableHead>
            <TableRow>
                {hasExpandableRows && <TableCell width={40} />}
                {selection && <TableCell width={40} />}
                {columns.map((column) => {
                    const hasSortableColumns = columns.some((col) => col.sortable !== false);
                    const isSortable = column.sortable !== false && hasSortableColumns;
                    const isActiveSort = activeSortBy === column.value;

                    return (
                        <TableCell
                            key={column.value}
                            width={column.width}
                            align={column.align}
                            onClick={() => isSortable && handleSort(column.value)}
                            sx={{
                                cursor: isSortable ? 'pointer' : 'default',
                                userSelect: 'none',
                                position: 'relative',
                                '&:hover': isSortable
                                    ? {
                                          backgroundColor: 'action.hover',
                                          '& .sort-icon': {
                                              opacity: 1
                                          }
                                      }
                                    : {}
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: 0.5
                                }}
                            >
                                {column.text}
                                {isSortable && (
                                    <Box
                                        className="sort-icon"
                                        sx={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            opacity: isActiveSort ? 1 : 0,
                                            transition: 'opacity 0.2s',
                                            color: isActiveSort ? 'text.primary' : 'text.disabled',
                                            fontSize: '18px'
                                        }}
                                    >
                                        {isActiveSort && activeSortDirection === 'asc' && (
                                            <ArrowUpward fontSize="inherit" />
                                        )}
                                        {isActiveSort && activeSortDirection === 'desc' && (
                                            <ArrowDownward fontSize="inherit" />
                                        )}
                                        {!isActiveSort && <ArrowUpward fontSize="inherit" />}
                                    </Box>
                                )}
                            </Box>
                        </TableCell>
                    );
                })}
            </TableRow>
        </TableHead>
    );

    const renderTableBody = () => {
        if (!paginatedData || paginatedData.length === 0) return renderEmptyState();
        return (
            <TableBody>
                {paginatedData.map((row, rowIndex) => {
                    const isSelected = isRowSelected(row);
                    const isExpanded = isRowExpanded(row);
                    const rowProps = getRowProps?.(row, rowIndex) || {};
                    const showExpandIcon = expansion?.showExpandIconCondition?.(row) ?? true;

                    return (
                        <React.Fragment key={expansion?.getRowKey(row) || rowIndex}>
                            <TableRow
                                hover
                                selected={isSelected}
                                onClick={(e: React.MouseEvent) => {
                                    if (selection && !expansion) {
                                        handleRowSelection(row, e);
                                    }
                                    onRowClick?.(row, rowIndex);
                                }}
                                sx={{
                                    cursor: onRowClick || selection ? 'pointer' : 'default',
                                    ...rowProps.sx
                                }}
                                className={`data-row ${rowIndex % 2 === 0 ? 'even-row' : 'odd-row'}`}
                                {...rowProps}
                            >
                                {hasExpandableRows && (
                                    <TableCell>
                                        {showExpandIcon && (
                                            <IconButton
                                                size="small"
                                                onClick={(e: React.MouseEvent) => handleRowExpansion(row, e)}
                                            >
                                                {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                                            </IconButton>
                                        )}
                                    </TableCell>
                                )}
                                {selection && (
                                    <TableCell padding="checkbox">
                                        <WcnCheckbox
                                            checked={isSelected}
                                            onClick={(e: React.MouseEvent) => handleRowSelection(row, e)}
                                        />
                                    </TableCell>
                                )}
                                {columns.map((column, colIndex) => {
                                    const cellTitle =
                                        typeof column.title === 'function' ? column.title(row) : column.title;
                                    return (
                                        <TableCell
                                            key={column.value}
                                            align={column.align || 'left'}
                                            className={`data-row ${colIndex % 2 === 0 ? 'even-row' : 'odd-row'}`}
                                            title={cellTitle}
                                        >
                                            {column.render ? column.render(row, rowIndex) : (row as any)[column.value]}
                                        </TableCell>
                                    );
                                })}
                            </TableRow>
                            {expansion && isExpanded && (
                                <TableRow>
                                    <TableCell
                                        colSpan={colSpan}
                                        className="shop-expanded-row"
                                        sx={{ p: 0, borderBottom: 0 }}
                                    >
                                        <Collapse in={isExpanded} timeout="auto" unmountOnExit>
                                            <Box>
                                                {expansion.nestedTable && (
                                                    <Box sx={{ pl: expansion.nestedTable.paddingLeft ?? 8 }}>
                                                        <WcnTable
                                                            columns={expansion.nestedTable.columns}
                                                            data={expansion.nestedTable.getChildRows(
                                                                row,
                                                                expansion.nestedTable.data
                                                            )}
                                                            size={expansion.nestedTable.size ?? 'small'}
                                                            noDataMessage={expansion.nestedTable.noDataMessage}
                                                            expansion={expansion.nestedTable.expansion}
                                                        />
                                                    </Box>
                                                )}
                                                {!expansion.nestedTable && expansion.renderExpandedContent?.(row)}
                                            </Box>
                                        </Collapse>
                                    </TableCell>
                                </TableRow>
                            )}
                        </React.Fragment>
                    );
                })}
            </TableBody>
        );
    };

    return (
        <Box
            className="data-table__wrapper"
            sx={{
                borderRadius: 1,
                overflow: 'hidden',
                border: '1px solid',
                borderColor: 'divider'
            }}
        >
            {title && (
                <Toolbar
                    sx={{
                        borderBottom: 1,
                        borderColor: 'divider',
                        minHeight: '48px !important',
                        px: 2,
                        bgcolor: 'transparent'
                    }}
                >
                    <Typography variant="h6" component="div">
                        {title}
                    </Typography>
                </Toolbar>
            )}
            <Box sx={{ position: 'relative' }}>
                {loading && (
                    <LinearProgress
                        sx={{
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            right: 0,
                            zIndex: 2
                        }}
                    />
                )}
                <TableContainer>
                    <Table
                        size={size}
                        sx={(theme: Theme) => ({
                            backgroundColor: theme.palette.background.paper,
                            '& .MuiTableCell-root': {
                                height: '60px',
                                fontSize: '14px',
                                whiteSpace: 'nowrap',
                                borderBottom: 0,
                                verticalAlign: 'middle'
                            },
                            '& .MuiTableHead-root .MuiTableCell-root': {
                                fontWeight: 500,
                                backgroundColor: theme.palette.action.hover
                            }
                        })}
                    >
                        {renderTableHead()}
                        {renderTableBody()}
                    </Table>
                </TableContainer>
            </Box>
            {pagination && (
                <Box>
                    <TablePagination
                        component="div"
                        count={pagination.count}
                        page={pagination.page ?? page}
                        onPageChange={pagination.onPageChange ?? handlePageChange}
                        rowsPerPage={pagination.rowsPerPage ?? rowsPerPage}
                        onRowsPerPageChange={pagination.onRowsPerPageChange ?? handleRowsPerPageChange}
                        rowsPerPageOptions={pagination.rowsPerPageOptions || [5, 10, 15]}
                        showFirstButton={false}
                        showLastButton={false}
                        labelRowsPerPage={t('rowsPerPage')}
                        labelDisplayedRows={(paginationInfo) => {
                            return `${paginationInfo.from}-${paginationInfo.to} ${t('of')} ${paginationInfo.count}`;
                        }}
                        sx={{
                            '.MuiTablePagination-toolbar': {
                                minHeight: '48px',
                                pl: 2
                            },
                            '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
                                m: 0
                            }
                        }}
                    />
                </Box>
            )}
        </Box>
    );
};

WcnTable.displayName = 'WcnTable';

export default WcnTable;
